import React, { useEffect, useState } from "react";
import {
  Alert,
  Card,
  Descriptions,
  Input,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { fmtDay } from "../institutionMerge.constants";
import { ActionTag } from "./RecordsTable";
import {
  ACTION_COL,
  AFTER_COL,
  BEFORE_COL,
  BeforeAfterLegend,
  cellClass,
} from "./beforeAfter";
import {
  AfterControl,
  SUBROLE_OPTIONS,
  credentialsText,
  keyOf,
  refLine,
  showValue,
  unionOf,
} from "./userFieldControl";
import { differingOf } from "./pending";

const { Text, Paragraph } = Typography;

const ROLE_WARNING =
  "Rôles différents — le compte conservé n'hérite pas automatiquement des droits de l'autre";

/** Decided AFTER values of a group: `fields`, plus the legacy `roles` (still accepted by the server). */
const decidedFields = d => ({
  ...(d.roles && d.roles.role !== undefined ? { role: d.roles.role } : {}),
  ...(d.roles && d.roles.subRoles ? { subRoles: d.roles.subRoles } : {}),
  ...(d.fields || {}),
});

const validEmail = e => typeof e === "string" && /^[^\s@]+@[^\s@]+$/.test(e);
const validRename = (g, r) =>
  !!r &&
  g.accounts.some(a => a._id === r.accountId) &&
  validEmail(r.email) &&
  r.email.trim().toLowerCase() !== g.email.toLowerCase();

/** What the loser's e-mail becomes once archived (server: `local+merged-<id>@domain`). */
const neutralised = email => {
  const i = email.indexOf("@");
  return i < 0
    ? `${email}+merged-…`
    : `${email.slice(0, i)}+merged-…${email.slice(i)}`;
};

/** The server's suggestion for a differing path — recomputed from the chosen survivor when it is not the suggested one. */
const suggestionFor = (g, f, survivorId) => {
  if (f.kind === "account") return survivorId;
  if (f.kind === "refs") return unionOf(g.accounts, f.path);
  if (
    g.suggested &&
    survivorId === g.suggestedSurvivor &&
    f.path in g.suggested
  )
    return g.suggested[f.path];
  const s = g.accounts.find(a => a._id === survivorId);
  return s ? (s.fields || {})[f.path] : undefined;
};

/** New e-mail for « Garder séparés » — saved on blur / Enter, must contain an @ and differ from the shared one. */
function RenameInput({ g, rename, disabled, onSave }) {
  const saved = (rename && rename.email) || "";
  const [draft, setDraft] = useState(saved);
  useEffect(() => setDraft(saved), [saved]);
  const v = draft.trim();
  const invalid =
    v !== "" && (!validEmail(v) || v.toLowerCase() === g.email.toLowerCase());
  const commit = () => {
    if (v === saved) return;
    onSave(v);
  };
  return (
    <Space direction="vertical" size={0}>
      <Input
        disabled={disabled}
        style={{ width: 300 }}
        status={invalid ? "error" : undefined}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onPressEnter={e => e.target.blur()}
        placeholder="nouvelle@adresse.ch"
      />
      {invalid && (
        <Text type="danger">
          {validEmail(v)
            ? "identique à l'adresse actuelle"
            : "adresse e-mail invalide"}
        </Text>
      )}
    </Space>
  );
}

/** One duplicated e-mail: decision, the accounts (before), every field before/after, the result. */
function DuplicateGroup({
  g,
  d,
  fieldsList,
  refNames,
  principalOptions,
  disabled,
  set,
}) {
  const [scope, setScope] = useState("all");
  const ctx = { refNames, accounts: g.accounts };
  const decided = decidedFields(d);
  // older previews carry no `differing`: compared locally — the same helper as the step counters (pending.js)
  const differing = differingOf(g, fieldsList);
  // a differing path the field list does not describe still needs its decision: show it as a plain text row
  const fields =
    fieldsList &&
    fieldsList.concat(
      differing
        .filter(p => !fieldsList.some(f => f.path === p))
        .map(p => ({ path: p, label: p, kind: "text" })),
    );
  const survivorId = d.action === "merge" ? d.survivorId : null;
  const survivor = g.accounts.find(a => a._id === survivorId);
  const others = g.accounts.filter(a => a._id !== survivorId);
  const pending = differing.filter(p => decided[p] === undefined);
  const isDecided =
    d.action === "merge"
      ? !!survivor && pending.length === 0
      : d.action === "keep"
      ? validRename(g, d.rename)
      : false;
  const setAction = action =>
    set(
      g.email,
      action === "merge"
        ? {
            action,
            survivorId: d.survivorId || g.suggestedSurvivor,
            fields: d.fields || {},
          }
        : { action },
    );
  const saveField = (path, value) =>
    set(g.email, { fields: { ...decided, [path]: value } });
  const setRename = patch =>
    set(g.email, { rename: { ...(d.rename || {}), ...patch } });

  const accountColumns = [
    {
      title: "Compte",
      key: "account",
      render: (_, a) => (
        <Space direction="vertical" size={0}>
          <Text strong>{a.name || <Text type="secondary">sans nom</Text>}</Text>
          <Text type="secondary">
            {a.institution.name}
            {a.institution.isTarget
              ? " (institution qui reçoit la fusion)"
              : ""}
            {a.userId ? ` · n° ${a.userId}` : ""}
          </Text>
          {(a.otherInstitutions || []).length > 0 && (
            <Text type="secondary">
              aussi rattaché à :{" "}
              {a.otherInstitutions.map(i => i.name).join(", ")}
            </Text>
          )}
          {a.phone && <Text type="secondary">{a.phone}</Text>}
        </Space>
      ),
    },
    {
      title: "Rôle",
      key: "role",
      render: (_, a) => (
        <Space size={4} wrap>
          {a.role}
          {(a.subRoles || []).map(s => (
            <Tag key={s}>{s}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Statut",
      key: "status",
      render: (_, a) => (
        <Space direction="vertical" size={0}>
          <Tag
            color={
              a.archived || a.status === "archived"
                ? "default"
                : a.status === "active"
                ? "green"
                : "orange"
            }
          >
            {a.status}
            {a.archived ? " · archivé" : ""}
          </Tag>
          <Text type="secondary">
            {a.confirmed ? "compte confirmé" : "compte non confirmé"}
          </Text>
          {a.hasPassword !== undefined && (
            <Text type="secondary">
              {a.hasPassword ? "mot de passe défini" : "aucun mot de passe"}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Dates",
      key: "dates",
      render: (_, a) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary">créé le {fmtDay(a.createdAt)}</Text>
          <Text type={a.lastLogin ? undefined : "secondary"}>
            dernière connexion {a.lastLogin ? fmtDay(a.lastLogin) : "jamais"}
          </Text>
          <Text type="secondary">dernière action {fmtDay(a.lastAction)}</Text>
        </Space>
      ),
    },
    {
      title: "Rattachements",
      key: "references",
      render: (_, a) =>
        a.references ? (
          <Space direction="vertical" size={0}>
            <Text strong>{a.references}</Text>
            {(a.referencesBy || []).map(r => (
              <Text key={`${r.model}.${r.field}`} type="secondary">
                {refLine(r)}
              </Text>
            ))}
          </Space>
        ) : (
          <Text type="secondary">aucun</Text>
        ),
    },
    ...(d.action !== "keep"
      ? [
          {
            title: "Conserver",
            key: "keep",
            width: 130,
            ...ACTION_COL,
            render: (_, a) => (
              <Space size={4}>
                {d.action === "merge" && (
                  <Radio
                    disabled={disabled}
                    checked={survivorId === a._id}
                    onChange={() => set(g.email, { survivorId: a._id })}
                  />
                )}
                {a._id === g.suggestedSurvivor && <Tag>suggestion</Tag>}
              </Space>
            ),
          },
        ]
      : []),
  ];

  const instNames = g.accounts.map(a => a.institution.name);
  const columnTitle = a =>
    `Avant — ${a.institution.name}${
      instNames.filter(n => n === a.institution.name).length > 1
        ? ` (${a.name || `n° ${a.userId}`})`
        : ""
    }`;
  const fieldColumns = fields && [
    {
      title: "Champ",
      key: "field",
      width: 220,
      render: (_, f) => {
        const dec = decided[f.path];
        const isDiff = differing.includes(f.path);
        return (
          <Space direction="vertical" size={0}>
            <Space size={6} wrap>
              <Text>{f.label || f.path}</Text>
              {dec !== undefined ? (
                <Tag color="blue">choix manuel</Tag>
              ) : isDiff ? (
                <Tag color="red">à choisir</Tag>
              ) : (
                <Tag>identique</Tag>
              )}
            </Space>
            {g.rolesDiffer && ["role", "subRoles"].includes(f.path) && (
              <Text type="warning">{ROLE_WARNING}</Text>
            )}
          </Space>
        );
      },
    },
    ...g.accounts.map(a => ({
      title: columnTitle(a),
      key: a._id,
      onHeaderCell: () => ({ className: "merge-col-before" }),
      onCell: f => ({
        className: cellClass("merge-col-before", differing.includes(f.path)),
      }),
      render: (_, f) => {
        const node =
          f.kind === "account"
            ? credentialsText(a)
            : showValue((a.fields || {})[f.path], f, ctx);
        // the value the kept account will hold; a before-value it replaces is struck through
        const after =
          decided[f.path] !== undefined
            ? decided[f.path]
            : differing.includes(f.path)
            ? undefined
            : f.kind === "account"
            ? (survivor || g.accounts[0])._id
            : ((survivor || g.accounts[0]).fields || {})[f.path];
        const mine = f.kind === "account" ? a._id : (a.fields || {})[f.path];
        const kMine = f.kind === "account" ? String(mine) : keyOf(mine, f);
        const kAfter =
          after === undefined
            ? null
            : f.kind === "account"
            ? String(after)
            : keyOf(after, f);
        return kAfter !== null && kAfter !== kMine ? (
          <Text delete type="secondary">
            {node}
          </Text>
        ) : (
          node
        );
      },
    })),
    {
      title: `Après — compte conservé${
        survivor ? ` (${survivor.institution.name})` : ""
      }`,
      key: "after",
      width: "38%",
      ...AFTER_COL,
      render: (_, f) => {
        const dec = decided[f.path];
        if (!differing.includes(f.path))
          return (
            <Text strong>
              {showValue(
                dec !== undefined
                  ? dec
                  : f.kind === "account"
                  ? (survivor || g.accounts[0])._id
                  : ((survivor || g.accounts[0]).fields || {})[f.path],
                f,
                ctx,
              )}
            </Text>
          );
        return (
          <AfterControl
            f={f}
            accounts={g.accounts}
            decided={dec}
            suggestion={suggestionFor(g, f, survivorId || g.suggestedSurvivor)}
            disabled={disabled}
            onSave={v => saveField(f.path, v)}
            refNames={refNames}
            principalOptions={principalOptions}
          />
        );
      },
    },
  ];
  const fieldRows =
    fields &&
    (scope === "diff"
      ? fields.filter(f => differing.includes(f.path))
      : fields);

  const received = others.reduce((n, a) => n + (a.references || 0), 0);
  const renamed =
    d.rename && g.accounts.find(a => a._id === d.rename.accountId);

  return (
    <Card
      size="small"
      title={
        <Space wrap>
          <Text code>{g.email}</Text>
          <Tag>{g.accounts.length} comptes</Tag>
          {g.rolesDiffer && <Tag color="orange">rôles différents</Tag>}
          <Tag color={isDecided ? "green" : "red"}>
            {isDecided ? "décidé" : "à décider"}
          </Tag>
          {d.action === "merge" && pending.length > 0 && (
            <Text type="secondary">{pending.length} champ(s) à choisir</Text>
          )}
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Radio.Group
          disabled={disabled}
          value={d.action || null}
          onChange={e => setAction(e.target.value)}
        >
          <Radio value="merge">Fusionner — un seul compte survit</Radio>
          <Radio value="keep">
            Garder séparés — personnes différentes, renommer un e-mail
          </Radio>
        </Radio.Group>

        <div>
          <Text strong>Comptes</Text>
          <Table
            style={{ marginTop: 6 }}
            size="small"
            pagination={false}
            rowKey="_id"
            columns={accountColumns}
            dataSource={g.accounts}
          />
        </div>

        {d.action === "merge" && fieldsList && (
          <div>
            <Space wrap style={{ marginBottom: 6 }}>
              <Text strong>Champs — avant / après</Text>
              <Radio.Group
                size="small"
                optionType="button"
                value={scope}
                onChange={e => setScope(e.target.value)}
                options={[
                  { value: "all", label: `Tous les champs (${fields.length})` },
                  {
                    value: "diff",
                    label: `Champs différents (${differing.length})`,
                  },
                ]}
              />
              <Text type="secondary">
                pour chaque champ différent, choisissez ce que garde le compte
                conservé — une valeur existante ou « Autre… »
              </Text>
            </Space>
            <Table
              size="small"
              pagination={false}
              rowKey="path"
              columns={fieldColumns}
              dataSource={fieldRows}
            />
          </div>
        )}

        {d.action === "merge" && !fieldsList && g.rolesDiffer && (
          <Alert
            type="warning"
            showIcon
            message="Les deux comptes n'ont pas les mêmes droits. Le compte conservé ne reçoit PAS automatiquement la réunion des deux — choisissez explicitement."
            description={
              <Space wrap>
                <Text>rôle</Text>
                <Select
                  disabled={disabled}
                  style={{ width: 160 }}
                  value={d.roles && d.roles.role}
                  onChange={role =>
                    set(g.email, { roles: { ...(d.roles || {}), role } })
                  }
                  options={[...new Set(g.accounts.map(a => a.role))].map(r => ({
                    value: r,
                    label: r,
                  }))}
                />
                <Text>sous-rôles</Text>
                <Select
                  disabled={disabled}
                  mode="multiple"
                  style={{ minWidth: 320 }}
                  value={d.roles && d.roles.subRoles}
                  onChange={subRoles =>
                    set(g.email, { roles: { ...(d.roles || {}), subRoles } })
                  }
                  options={SUBROLE_OPTIONS.map(v => ({ value: v, label: v }))}
                />
              </Space>
            }
          />
        )}

        {d.action === "keep" && (
          <Space wrap align="start">
            <Text>Renommer le compte</Text>
            <Select
              disabled={disabled}
              style={{ width: 280 }}
              value={d.rename && d.rename.accountId}
              onChange={accountId => setRename({ accountId })}
              options={g.accounts.map(a => ({
                value: a._id,
                label: `${a.institution.name} — ${a.name || a._id}`,
              }))}
              placeholder="lequel ?"
            />
            <Text>en</Text>
            <RenameInput
              g={g}
              rename={d.rename}
              disabled={disabled}
              onSave={email => setRename({ email })}
            />
          </Space>
        )}

        {d.action && (
          <div>
            <Text strong>Résultat</Text>
            {d.action === "merge" &&
              (survivor ? (
                <Paragraph style={{ margin: "6px 0 0" }}>
                  Compte conservé :{" "}
                  <Text strong>{survivor.name || "sans nom"}</Text> (
                  {survivor.institution.name}) reçoit {received} rattachement(s)
                  de {others.map(o => o.institution.name).join(", ")} ;{" "}
                  {others.length > 1
                    ? "les autres comptes sont archivés et leur e-mail neutralisé"
                    : "l'autre compte est archivé et son e-mail neutralisé"}{" "}
                  (<Text code>{g.email}</Text> →{" "}
                  <Text code>{neutralised(g.email)}</Text>).
                </Paragraph>
              ) : (
                <Paragraph type="secondary" style={{ margin: "6px 0 0" }}>
                  Choisissez le compte à conserver.
                </Paragraph>
              ))}
            {d.action === "merge" && survivor && fieldsList && (
              <Descriptions
                size="small"
                bordered
                column={{ xs: 1, md: 2, xl: 3 }}
                style={{ marginTop: 8 }}
              >
                {fields.map(f => {
                  const dec = decided[f.path];
                  const open = differing.includes(f.path) && dec === undefined;
                  return (
                    <Descriptions.Item key={f.path} label={f.label || f.path}>
                      {open ? (
                        <Text type="danger">à choisir</Text>
                      ) : (
                        <Space size={6} wrap>
                          {showValue(
                            dec !== undefined
                              ? dec
                              : f.kind === "account"
                              ? survivor._id
                              : (survivor.fields || {})[f.path],
                            f,
                            ctx,
                          )}
                          {dec !== undefined && (
                            <Tag color="blue">choix manuel</Tag>
                          )}
                        </Space>
                      )}
                    </Descriptions.Item>
                  );
                })}
              </Descriptions>
            )}
            {d.action === "keep" && (
              <div style={{ marginTop: 6 }}>
                <Paragraph style={{ margin: 0 }}>
                  Les {g.accounts.length} comptes restent séparés, aucun n'est
                  archivé ; un seul change d'adresse e-mail.
                </Paragraph>
                {g.accounts.map(a => (
                  <div key={a._id}>
                    {a.institution.name} — {a.name || "sans nom"} :{" "}
                    <Text code>{g.email}</Text>
                    {renamed && renamed._id === a._id ? (
                      validRename(g, d.rename) ? (
                        <>
                          {" "}
                          → <Text code>{d.rename.email.trim()}</Text>
                        </>
                      ) : (
                        <Text type="danger"> → nouvelle adresse à saisir</Text>
                      )
                    ) : (
                      <Text type="secondary"> (inchangé)</Text>
                    )}
                  </div>
                ))}
                {!renamed && (
                  <Text type="danger">
                    Choisissez le compte à renommer et sa nouvelle adresse
                    e-mail.
                  </Text>
                )}
              </div>
            )}
          </div>
        )}
      </Space>
    </Card>
  );
}

const OTHERS_ACTION = { code: "repoint", label: "Rattaché" };
const OTHERS_DETAIL =
  "aucune décision nécessaire : le compte suit son établissement";

const statusColor = a =>
  a.archived || a.status === "archived"
    ? "default"
    : a.status === "active"
    ? "green"
    : "orange";

/**
 * Every account of the sources that is NOT in a duplicate group: nothing to decide, it simply
 * follows its institution into the result — listed so the operator sees the whole picture.
 */
function OthersTable({ others, preview, targetName }) {
  const clusterIds = new Set(
    (preview.sources || [])
      .map(s => s._id)
      .concat(preview.existingTarget ? [preview.existingTarget._id] : []),
  );
  const instName = i => (i && i.name) || "—";
  const afterInstitutions = a => {
    const out = [];
    let targetAdded = false;
    (a.otherInstitutions || []).forEach(i => {
      if (clusterIds.has(i._id)) {
        if (!targetAdded) {
          out.push(targetName);
          targetAdded = true;
        }
      } else out.push(i.name);
    });
    return out;
  };

  const columns = [
    {
      title: "Compte",
      key: "account",
      width: "30%",
      render: (_, a) => (
        <Space direction="vertical" size={0}>
          <Text strong>{a.name || <Text type="secondary">sans nom</Text>}</Text>
          <Text type="secondary">
            {a.email}
            {a.userId ? ` · n° ${a.userId}` : ""}
          </Text>
          <Space size={4} wrap>
            {a.role}
            {(a.subRoles || []).map(s => (
              <Tag key={s}>{s}</Tag>
            ))}
            <Tag color={statusColor(a)}>
              {a.status}
              {a.archived ? " · archivé" : ""}
            </Tag>
          </Space>
        </Space>
      ),
    },
    {
      title: "Avant",
      key: "before",
      ...BEFORE_COL,
      render: (_, a) => (
        <Space direction="vertical" size={0}>
          <Text delete type="secondary">
            {instName(a.institution)}
          </Text>
          {(a.otherInstitutions || []).length > 0 && (
            <Text type="secondary">
              aussi rattaché à :{" "}
              {a.otherInstitutions.map(i => i.name).join(", ")}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Après",
      key: "after",
      ...AFTER_COL,
      render: (_, a) => {
        const after = afterInstitutions(a);
        return (
          <Space direction="vertical" size={0}>
            <Text strong>{targetName}</Text>
            {after.length > 0 && (
              <Text type="secondary">
                aussi rattaché à : {after.join(", ")}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 260,
      ...ACTION_COL,
      render: () => (
        <ActionTag action={{ ...OTHERS_ACTION, detail: OTHERS_DETAIL }} />
      ),
    },
  ];

  return (
    <div>
      <Text strong>Autres comptes — {others.length}</Text>
      <Table
        style={{ marginTop: 6 }}
        size="small"
        rowKey="_id"
        columns={columns}
        dataSource={others}
        locale={{ emptyText: "Aucun autre compte" }}
        pagination={{
          pageSize: 20,
          size: "small",
          hideOnSinglePage: true,
          showTotal: (t, [a, b]) => `${a}–${b} sur ${t}`,
        }}
      />
    </div>
  );
}

/**
 * Step 3 — duplicate accounts. One card per shared e-mail; apply stays blocked until every
 * group is decided: merge = survivor + an explicit AFTER value for every differing field,
 * keep = one account renamed. Roles are never combined silently.
 * Below the groups, every other account of the sources is listed with its action « Rattaché ».
 */
export function StepUsers({ preview, decisions, onChange, disabled }) {
  const groups = (preview.users && preview.users.duplicates) || [];
  const others = Array.isArray(preview.users && preview.users.others)
    ? preview.users.others
    : null;
  const targetName =
    (decisions && decisions.targetName) ||
    (preview.existingTarget && preview.existingTarget.name) ||
    "l'institution résultante";
  const othersSection = others && (
    <OthersTable others={others} preview={preview} targetName={targetName} />
  );

  if (!groups.length)
    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Alert
          type="success"
          showIcon
          message="Aucun compte en double — la même adresse e-mail n'existe pas sur plusieurs établissements."
        />
        {othersSection}
      </Space>
    );

  const users = decisions.users || {};
  // the wizard replaces `users[email]` as a whole: always send the full group decision
  const set = (email, patch) =>
    onChange({ users: { [email]: { ...(users[email] || {}), ...patch } } });
  const fieldsList =
    Array.isArray(preview.users.fields) && preview.users.fields.length
      ? preview.users.fields
      : null;
  const refNames = {
    ...((preview.config && preview.config.refNames) || {}),
    ...(preview.users.refNames || {}),
  };
  const principals =
    preview.config &&
    preview.config.options &&
    preview.config.options.Principal;
  const principalOptions = Array.isArray(principals)
    ? principals.map(p => ({ value: p._id, label: p.name }))
    : null;

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Alert
        type="warning"
        showIcon
        message={`${groups.length} adresse(s) e-mail présentes sur plusieurs établissements.`}
        description="Fusionnés, ces comptes se retrouveraient dans une seule institution et la connexion choisirait l'un d'eux au hasard. Décidez pour chaque groupe. Le compte conservé reçoit les prestations et factures des deux ; l'autre est archivé et son e-mail neutralisé. Pour chaque champ qui diffère, choisissez ce que garde le compte conservé : une des valeurs existantes ou une valeur personnalisée. Les autres comptes sont simplement rattachés à l'institution résultante — ils sont listés plus bas."
      />
      <BeforeAfterLegend />
      {groups.map(g => (
        <DuplicateGroup
          key={g.email}
          g={g}
          d={users[g.email] || {}}
          fieldsList={fieldsList}
          refNames={refNames}
          principalOptions={principalOptions}
          disabled={disabled}
          set={set}
        />
      ))}
      {othersSection}
    </Space>
  );
}
